import { useEffect, useState } from 'react';
import { ArrowLeft, Flag, CaretLeft, CaretRight, TextAa, Check, X } from '@phosphor-icons/react';
import { T, fs, fx, FONT_SCALE_KEY, FONT_SCALE_STEPS } from '../test-session/skins/examTokens';
import {
    page, barMobile, mobileNav, mobileNavItem, mobileNavDot, bar, barBtn, toolBtn, srOnly,
    radioDot, radioInner, navCol, navItem, mainCol, optionsCard, optionRow, actionBtn,
} from '../test-session/skins/examStyles';

/**
 * Пробник NBME в экзаменационном скине (владелец, 08.09.2026: «все пробники
 * должны иметь дизайн exam»). Внешность — из examTokens/examStyles, как у
 * ExamView, но без разбора и правильных ответов: настоящий экзамен ничего не
 * показывает до конца формы. Ничего не знает про API — всё приходит пропсами.
 */
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const fmtClock = (s) => { const v = Math.max(0, s | 0); return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`; };

export default function MockExamView({
    attempt, items, blocksTotal = 4, currentIndex, selected, marked, secondsLeft,
    busy, error, isMobile, onSelect, onNavigate, onToggleMark, onSubmitBlock, onExit,
}) {
    const [scale, setScale] = useState(() => {
        const saved = parseFloat(localStorage.getItem(FONT_SCALE_KEY));
        return FONT_SCALE_STEPS.includes(saved) ? saved : 1;
    });
    useEffect(() => { localStorage.setItem(FONT_SCALE_KEY, String(scale)); }, [scale]);
    const [hoveredNav, setHoveredNav] = useState(null);
    const stepScale = (dir) => setScale((cur) => {
        const i = FONT_SCALE_STEPS.indexOf(cur);
        return FONT_SCALE_STEPS[Math.min(FONT_SCALE_STEPS.length - 1, Math.max(0, i + dir))];
    });

    const total = items.length;
    const item = items[currentIndex] || items[0];
    const urgent = secondsLeft < 60;
    const label = (text) => (isMobile ? null : <span style={fx(T.type.tool)}>{text}</span>);
    const contentPad = isMobile ? 16 : T.layout.contentPad;
    const answeredCount = items.filter((it) => selected[it.id]).length;

    const prevNext = (
        <>
            <button style={toolBtn} onClick={() => onNavigate(currentIndex - 1)} disabled={currentIndex === 0} title="Предыдущий">
                <CaretLeft size={22} weight="fill" />{label('Назад')}
            </button>
            <button style={toolBtn} onClick={() => onNavigate(currentIndex + 1)} disabled={currentIndex >= total - 1} title="Следующий">
                <CaretRight size={22} weight="fill" />{label('Вперёд')}
            </button>
        </>
    );

    const navState = (it, idx) => ({
        current: idx === currentIndex, answered: !!selected[it.id], marked: !!marked[it.id],
    });

    return (
        <div style={{ ...page, fontFamily: T.font, color: T.color.text }}>
            <header style={{ ...bar('top'), ...(isMobile ? barMobile : null) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 16, minWidth: 0 }}>
                    <button style={barBtn} onClick={onExit} title="К пробникам"><ArrowLeft size={20} /></button>
                    <div style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        <div style={{ ...fx(T.type.bar), fontWeight: 700 }}>
                            {isMobile ? `${currentIndex + 1} / ${total}` : `Вопрос ${currentIndex + 1} из ${total}`}
                        </div>
                        <div style={{ ...fx(T.type.small), color: T.color.barMuted }}>
                            Блок {attempt.block.index} из {blocksTotal}{isMobile ? '' : ` · ${attempt.form.title}`}
                        </div>
                    </div>
                    <button
                        style={{ ...barBtn, color: marked[item?.id] ? '#F5C451' : T.color.barText }}
                        onClick={() => onToggleMark(item.id)}
                        title="Пометить вопрос"
                    >
                        <Flag size={isMobile ? 20 : 16} weight={marked[item?.id] ? 'fill' : 'regular'} />
                        {!isMobile && <span style={{ ...fx(T.type.tool), fontWeight: 600 }}>Пометить</span>}
                    </button>
                </div>

                {!isMobile && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{prevNext}</div>}

                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 12 }}>
                    {!isMobile && (
                        <>
                            <button style={toolBtn} onClick={() => stepScale(-1)} title="Мельче"><TextAa size={16} />{label('Мельче')}</button>
                            <button style={toolBtn} onClick={() => stepScale(1)} title="Крупнее"><TextAa size={22} />{label('Крупнее')}</button>
                        </>
                    )}
                    <div data-testid="mock-timer" data-urgent={urgent ? 'true' : 'false'} style={{
                        ...fx(T.type.bar), fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        padding: '4px 10px', borderRadius: 4,
                        background: urgent ? '#B91C1C' : 'rgba(255,255,255,0.12)', color: '#FFFFFF',
                    }}>
                        {fmtClock(secondsLeft)}
                    </div>
                </div>
            </header>

            {isMobile && (
                <nav data-testid="mock-nav" style={mobileNav}>
                    {items.map((it, idx) => {
                        const st = navState(it, idx);
                        return (
                            <button key={it.id} data-testid={`nav-${it.id}`} data-answered={String(st.answered)} data-marked={String(st.marked)}
                                ref={st.current ? (el) => el?.scrollIntoView?.({ inline: 'center', block: 'nearest' }) : undefined}
                                onClick={() => onNavigate(idx)}
                                style={{ ...mobileNavItem, background: st.current ? T.color.navCurrent : 'transparent', fontWeight: st.current ? 700 : 500 }}>
                                <span>{idx + 1}</span>
                                <span style={{ ...mobileNavDot, background: st.marked ? '#F5C451' : st.answered ? T.color.accent : 'transparent' }} />
                            </button>
                        );
                    })}
                </nav>
            )}

            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {!isMobile && (
                    <nav data-testid="mock-nav" style={navCol}>
                        {items.map((it, idx) => {
                            const st = navState(it, idx);
                            return (
                                <button key={it.id} data-testid={`nav-${it.id}`} data-answered={String(st.answered)} data-marked={String(st.marked)}
                                    onClick={() => onNavigate(idx)}
                                    onMouseEnter={() => setHoveredNav(idx)}
                                    onMouseLeave={() => setHoveredNav((cur) => (cur === idx ? null : cur))}
                                    style={{
                                        ...navItem,
                                        background: st.current ? T.color.navCurrent
                                            : hoveredNav === idx ? T.color.navHover
                                            : idx % 2 === 0 ? T.color.navZebraOdd : T.color.navZebraEven,
                                    }}>
                                    <span style={{ width: T.layout.navIcon, height: T.layout.navIcon, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        color: st.marked ? '#D9A400' : st.answered ? T.color.accent : 'transparent' }}>
                                        {st.marked ? <Flag size={14} weight="fill" /> : st.answered ? <Check size={T.layout.navIcon} weight="bold" /> : null}
                                    </span>
                                    <span style={{ ...fx(T.type.nav), fontWeight: 500, color: T.color.navNumber }}>{idx + 1}</span>
                                </button>
                            );
                        })}
                    </nav>
                )}

                <main style={mainCol}>
                    <div style={{ padding: contentPad, maxWidth: 1200 }}>
                        {error && (
                            <div style={{ ...fx(T.type.small), color: T.color.incorrectText, border: `1px solid ${T.color.incorrectBorder}`, borderRadius: 4, padding: '8px 12px', marginBottom: 16 }}>{error}</div>
                        )}
                        {item && (
                            <>
                                <p style={{ ...fs(T.type.stem, scale), margin: '4px 0 20px', color: T.color.textStrong, whiteSpace: 'pre-wrap' }}>
                                    {item.question.text}
                                </p>
                                <div style={{ ...optionsCard, ...(isMobile ? { maxWidth: '100%' } : null) }}>
                                    {item.options.map((opt, i) => {
                                        const chosen = selected[item.id] === opt.id;
                                        return (
                                            <label key={opt.id} aria-label={`${LETTERS[i]}. ${opt.text}`}
                                                style={{ ...optionRow, ...fs(T.type.option, scale), background: 'transparent', cursor: 'pointer' }}>
                                                <input type="radio" name={`mock-item-${item.id}`} checked={chosen} onChange={() => onSelect(item.id, opt.id)} style={srOnly} />
                                                <span style={{ ...radioDot, borderColor: chosen ? T.color.radioSelBorder : T.color.radioBorder, background: chosen ? T.color.radioSelBg : T.color.radioBg }}>
                                                    {chosen && <span style={radioInner} />}
                                                </span>
                                                <span style={{ color: T.color.textStrong }}><strong>{LETTERS[i]}.</strong></span>
                                                <span style={{ color: T.color.textStrong }}>{opt.text}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>

            <footer data-testid="mock-footer" style={{ ...bar('bottom'), ...(isMobile ? barMobile : null) }}>
                {isMobile ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>{prevNext}</div>
                ) : (
                    <div style={{ ...fx(T.type.small), color: T.color.barMuted }}>
                        {attempt.form.title} · отвечено {answeredCount} из {total}
                    </div>
                )}
                <button
                    style={{ ...actionBtn, ...fx(T.type.option), background: busy ? '#94A3B8' : T.color.accent, cursor: busy ? 'not-allowed' : 'pointer' }}
                    onClick={onSubmitBlock} disabled={busy}
                >
                    Завершить блок
                </button>
            </footer>
        </div>
    );
}

/** Итог формы в том же оформлении: балл, процент и сетка вопросов по цвету. */
export function MockExamResults({ attempt, results, onExit }) {
    const pct = Math.round(parseFloat(results.percent) || 0);
    const items = results.items || [];
    return (
        <div style={{ ...page, fontFamily: T.font, color: T.color.text }}>
            <header style={bar('top')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button style={barBtn} onClick={onExit} title="К пробникам"><ArrowLeft size={20} /></button>
                    <div style={{ ...fx(T.type.bar), fontWeight: 700 }}>{attempt.form.title} · результат</div>
                </div>
            </header>
            <main style={{ ...mainCol, padding: T.layout.contentPad }}>
                <div style={{ maxWidth: 720 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
                        <span style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, color: pct >= 60 ? T.color.correctText : T.color.incorrectText }}>{pct}%</span>
                        <span style={{ ...fx(T.type.banner), color: T.color.muted }}>верно {results.raw_correct} из {results.total}</span>
                    </div>
                    <p style={{ ...fx(T.type.small), color: T.color.hint, margin: '0 0 20px' }}>
                        Балл считается по всей форме. Зелёные номера отвечены верно, красные нет, серые пропущены.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))', gap: 6, marginBottom: 24 }}>
                        {items.map((it) => {
                            const answered = !!it.answer?.selected_answer_id;
                            const color = !answered ? T.color.line : it.is_correct ? T.color.correct : T.color.incorrect;
                            return (
                                <div key={it.id} data-testid={`res-${it.id}`} data-correct={String(!!it.is_correct)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 32, borderRadius: 2,
                                        border: `1px solid ${color}`, color: answered ? color : T.color.hint, ...fx(T.type.small), fontWeight: 600 }}>
                                    {it.number}
                                    {answered && (it.is_correct ? <Check size={12} weight="bold" /> : <X size={12} weight="bold" />)}
                                </div>
                            );
                        })}
                    </div>
                    <button style={{ ...actionBtn, ...fx(T.type.option), background: T.color.accent }} onClick={onExit}>К пробникам</button>
                </div>
            </main>
        </div>
    );
}
